/**
 * UserDetailDrawer - Right-side drawer showing complete user information.
 * Displays profile, preferences, astro profile, and action buttons.
 */

import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { usersApi } from "@/lib/api/users";
import {
  X,
  User,
  Mail,
  Phone,
  Globe,
  Clock,
  MapPin,
  Smartphone,
  Monitor,
  Tablet,
  Star,
  Shield,
  FlaskConical,
  Calendar,
  Settings,
  Bell,
  BellOff,
  Sun,
  Moon,
  CreditCard,
  FolderOpen,
  UserCheck,
  UserX,
  ShieldOff,
  Home,
  Check,
  Pencil,
} from "lucide-react";
import type { UserCompleteView, UserRoleLevel } from "@/types/users";
import { useI18n } from "@/i18n";

interface UserDetailDrawerProps {
  isOpen: boolean;
  user: UserCompleteView | null;
  onClose: () => void;
  onToggleBeta?: (userId: string, isBeta: boolean) => void;
  onToggleAdmin?: (userId: string, isAdmin: boolean) => void;
  onUpdateRole?: (userId: string, roleLevel: UserRoleLevel) => void;
  onUpdateSystemRole?: (userId: string, systemRole: string) => void;
  onOpenResources?: (userId: string, userName: string) => void;
  onOpenSubscription?: (userId: string, userName: string) => void;
}

const ROLE_COLORS: Record<UserRoleLevel, string> = {
  free: "bg-gray-100 text-gray-800",
  vip: "bg-yellow-100 text-yellow-800",
  premium: "bg-purple-100 text-purple-800",
  pro: "bg-blue-100 text-blue-800",
};

// Helper function to get device icon
function getDeviceIcon(deviceMeta: Record<string, unknown> | null) {
  if (!deviceMeta) return null;
  const platform = (deviceMeta.platform as string)?.toLowerCase() || "";
  const deviceType = (deviceMeta.deviceType as string)?.toLowerCase() || "";

  if (
    platform.includes("ios") ||
    platform.includes("iphone") ||
    platform.includes("ipad")
  ) {
    if (deviceType.includes("tablet") || platform.includes("ipad")) {
      return { icon: Tablet, label: "iPad", color: "text-gray-600" };
    }
    return { icon: Smartphone, label: "iPhone", color: "text-gray-600" };
  }
  if (platform.includes("android")) {
    if (deviceType.includes("tablet")) {
      return { icon: Tablet, label: "Android Tablet", color: "text-green-600" };
    }
    return { icon: Smartphone, label: "Android", color: "text-green-600" };
  }
  if (platform.includes("web") || platform.includes("browser")) {
    return { icon: Monitor, label: "Web", color: "text-blue-600" };
  }
  return {
    icon: Monitor,
    label: platform || "Unknown",
    color: "text-gray-500",
  };
}

// Section component for consistent styling
function Section({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
}) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
        <Icon className="w-4 h-4 text-gray-500" />
        <h3 className="font-medium text-gray-900 text-sm">{title}</h3>
      </div>
      <div className="space-y-2">{children}</div>
    </Card>
  );
}

// Field component for key-value display
function Field({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: React.ReactNode;
  icon?: React.ElementType;
}) {
  return (
    <div className="flex items-start gap-2 text-sm">
      {Icon && <Icon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />}
      <div className="flex-1 min-w-0">
        <span className="text-gray-500">{label}:</span>
        <span className="ml-2 text-gray-900">
          {value || <span className="text-gray-300">-</span>}
        </span>
      </div>
    </div>
  );
}

export const UserDetailDrawer: React.FC<UserDetailDrawerProps> = ({
  isOpen,
  user,
  onClose,
  onToggleBeta,
  onToggleAdmin,
  onUpdateRole,
  onUpdateSystemRole,
  onOpenResources,
  onOpenSubscription,
}) => {
  const { messages, language } = useI18n();
  const t = messages.users;

  // Residence location inline editing
  const [isEditingResidence, setIsEditingResidence] = useState(false);
  const [residenceInput, setResidenceInput] = useState("");
  const [isSavingResidence, setIsSavingResidence] = useState(false);

  // Timezone inline editing
  const [isEditingBirthTz, setIsEditingBirthTz] = useState(false);
  const [birthTzInput, setBirthTzInput] = useState("");
  const [isSavingBirthTz, setIsSavingBirthTz] = useState(false);

  const [isEditingResidenceTz, setIsEditingResidenceTz] = useState(false);
  const [residenceTzInput, setResidenceTzInput] = useState("");
  const [isSavingResidenceTz, setIsSavingResidenceTz] = useState(false);

  // Reset editing state when user changes
  useEffect(() => {
    setIsEditingResidence(false);
    setResidenceInput(user?.residence_location || "");
    setIsEditingBirthTz(false);
    setBirthTzInput(user?.birth_timezone || "");
    setIsEditingResidenceTz(false);
    setResidenceTzInput(user?.residence_timezone || "");
  }, [user?.user_id, user?.birth_timezone, user?.residence_location, user?.residence_timezone]);

  // Helper function to format relative date with i18n
  const formatRelativeDate = (dateStr: string | null): string => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    const rd = t.relativeDates;
    if (diffDays === 0) return rd?.today || "Today";
    if (diffDays === 1) return rd?.yesterday || "Yesterday";
    if (diffDays < 7)
      return (rd?.daysAgo || "{count} days ago").replace(
        "{count}",
        String(diffDays),
      );
    if (diffDays < 30)
      return (rd?.weeksAgo || "{count} weeks ago").replace(
        "{count}",
        String(Math.floor(diffDays / 7)),
      );
    if (diffDays < 365)
      return (rd?.monthsAgo || "{count} months ago").replace(
        "{count}",
        String(Math.floor(diffDays / 30)),
      );
    return date.toLocaleDateString(language === "zh" ? "zh-CN" : "en-US");
  };

  // Helper function to format full date for tooltip
  const formatFullDate = (dateStr: string | null): string => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString(
      language === "zh" ? "zh-CN" : "en-US",
      {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      },
    );
  };

  const handleSaveResidence = async () => {
    if (!user?.user_id || !user?.astro_profile_id || !residenceInput.trim()) return;
    try {
      setIsSavingResidence(true);
      await usersApi.updateResidenceLocation(
        user.user_id,
        user.astro_profile_id,
        residenceInput.trim(),
      );
      // Update the displayed value optimistically
      user.residence_location = residenceInput.trim();
      setIsEditingResidence(false);
    } catch (error) {
      console.error("Failed to update residence location:", error);
      alert("Failed to update residence location");
    } finally {
      setIsSavingResidence(false);
    }
  };

  const handleSaveBirthTz = async () => {
    if (!user?.user_id || !user?.astro_profile_id || !birthTzInput.trim()) return;
    try {
      setIsSavingBirthTz(true);
      await usersApi.updateBirthTimezone(user.user_id, user.astro_profile_id, birthTzInput.trim());
      user.birth_timezone = birthTzInput.trim();
      setIsEditingBirthTz(false);
    } catch (error) {
      console.error("Failed to update birth timezone:", error);
      alert("Failed to update birth timezone");
    } finally {
      setIsSavingBirthTz(false);
    }
  };

  const handleSaveResidenceTz = async () => {
    if (!user?.user_id || !user?.astro_profile_id || !residenceTzInput.trim()) return;
    try {
      setIsSavingResidenceTz(true);
      await usersApi.updateResidenceTimezone(user.user_id, user.astro_profile_id, residenceTzInput.trim());
      user.residence_timezone = residenceTzInput.trim();
      setIsEditingResidenceTz(false);
    } catch (error) {
      console.error("Failed to update residence timezone:", error);
      alert("Failed to update residence timezone");
    } finally {
      setIsSavingResidenceTz(false);
    }
  };

  // Handle ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen || !user) return null;

  const deviceInfo = getDeviceIcon(user.pref_device_meta);
  const gpsLocation = user.pref_gps_location as Record<string, unknown> | null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-25 z-40"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 w-[480px] max-w-full bg-white shadow-xl z-50 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-gray-50">
          <div className="flex items-center gap-3">
            {/* Avatar */}
            {user.avatar_url ? (
              <img
                src={user.avatar_url}
                alt={user.display_name}
                className="w-12 h-12 rounded-full object-cover"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center">
                <User className="w-6 h-6 text-gray-400" />
              </div>
            )}
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {user.display_name}
              </h2>
              <p className="text-sm text-gray-500">@{user.username}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="p-1">
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Role & Flags */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Role Badge/Selector */}
            {onUpdateRole ? (
              <div className="relative">
                <select
                  value={user.role_level}
                  onChange={(e) =>
                    onUpdateRole(user.user_id, e.target.value as UserRoleLevel)
                  }
                  className={`appearance-none cursor-pointer inline-flex items-center gap-1.5 pl-8 pr-6 py-1 rounded-full text-sm font-medium border-2 border-transparent hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors ${
                    ROLE_COLORS[user.role_level as UserRoleLevel] ||
                    "bg-gray-100 text-gray-800"
                  }`}
                  title={t.drawer?.changeRole || "点击修改等级"}
                >
                  <option value="free">{t.roles.free}</option>
                  <option value="vip">{t.roles.vip}</option>
                  <option value="premium">{t.roles.premium}</option>
                  <option value="pro">{t.roles.pro}</option>
                </select>
                <CreditCard className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" />
              </div>
            ) : (
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${
                  ROLE_COLORS[user.role_level as UserRoleLevel] ||
                  "bg-gray-100 text-gray-800"
                }`}
              >
                <CreditCard className="w-4 h-4" />
                {t.roles[user.role_level as keyof typeof t.roles] ||
                  user.role_level}
              </span>
            )}

            {/* Admin Badge */}
            {user.is_admin && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                <Shield className="w-3.5 h-3.5" />
                {t.badges.admin}
              </span>
            )}

            {/* Beta Badge */}
            {user.is_beta_tester && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                <FlaskConical className="w-3.5 h-3.5" />
                Beta
              </span>
            )}

            {/* Astro Profile Badge */}
            {user.has_astro_profile && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                <Star className="w-3.5 h-3.5" />
                {t.badges.birthChart}
              </span>
            )}
          </div>

          {/* Profile Section */}
          <Section title={t.drawer?.profile || "基本信息"} icon={User}>
            <Field
              label="User ID"
              value={
                <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">
                  {user.user_id}
                </code>
              }
            />
            <Field
              label={t.drawer?.email || "邮箱"}
              value={user.email}
              icon={Mail}
            />
            <Field
              label={t.drawer?.phone || "手机"}
              value={user.phone_number}
              icon={Phone}
            />
            <Field
              label={t.drawer?.created || "注册时间"}
              value={
                <span title={formatFullDate(user.created_at)}>
                  {formatRelativeDate(user.created_at)}
                </span>
              }
              icon={Calendar}
            />
            <Field
              label={t.drawer?.updated || "最后更新"}
              value={
                <span title={formatFullDate(user.updated_at)}>
                  {formatRelativeDate(user.updated_at)}
                </span>
              }
              icon={Clock}
            />
            {user.username_update_count > 0 && (
              <Field
                label={t.drawer?.usernameUpdates || "用户名更新"}
                value={(
                  t.drawer?.usernameUpdateCountFormat ||
                  "{count} times ({date})"
                )
                  .replace("{count}", String(user.username_update_count))
                  .replace(
                    "{date}",
                    formatRelativeDate(user.username_updated_at),
                  )}
              />
            )}
          </Section>

          {/* Preferences Section */}
          <Section title={t.drawer?.preferences || "偏好设置"} icon={Settings}>
            <Field
              label={t.drawer?.timezone || "时区"}
              value={user.pref_timezone}
              icon={Clock}
            />
            <Field
              label={t.drawer?.locale || "语言区域"}
              value={user.pref_locale}
              icon={Globe}
            />
            <Field
              label={t.drawer?.language || "语言"}
              value={user.pref_language}
            />
            <Field
              label={t.drawer?.region || "地区"}
              value={user.pref_region_code}
              icon={MapPin}
            />
            <Field
              label={t.drawer?.currency || "货币"}
              value={user.pref_currency_code}
            />
            <Field
              label={t.drawer?.theme || "主题"}
              value={
                user.pref_theme && (
                  <span className="inline-flex items-center gap-1">
                    {user.pref_theme === "dark" ? (
                      <Moon className="w-3 h-3" />
                    ) : (
                      <Sun className="w-3 h-3" />
                    )}
                    {user.pref_theme}
                  </span>
                )
              }
            />
            <Field
              label={t.drawer?.fontSize || "字体大小"}
              value={user.pref_font_size}
            />
            <Field
              label={t.drawer?.pushNotification || "推送通知"}
              value={
                user.pref_push_notification !== null && (
                  <span
                    className={`inline-flex items-center gap-1 ${user.pref_push_notification ? "text-green-600" : "text-gray-400"}`}
                  >
                    {user.pref_push_notification ? (
                      <Bell className="w-3 h-3" />
                    ) : (
                      <BellOff className="w-3 h-3" />
                    )}
                    {user.pref_push_notification ? "已开启" : "已关闭"}
                  </span>
                )
              }
            />

            {/* Device Info */}
            {deviceInfo && (
              <Field
                label={t.drawer?.device || "设备"}
                value={
                  <span
                    className={`inline-flex items-center gap-1 ${deviceInfo.color}`}
                  >
                    <deviceInfo.icon className="w-3 h-3" />
                    {deviceInfo.label}
                    {user.pref_device_meta?.osVersion
                      ? ` (${String(user.pref_device_meta.osVersion)})`
                      : null}
                  </span>
                }
                icon={Smartphone}
              />
            )}

            {/* GPS Location */}
            {gpsLocation && (
              <Field
                label={t.drawer?.gpsLocation || "GPS位置"}
                value={
                  <span className="text-green-600">
                    {String(
                      gpsLocation.address ||
                        `${gpsLocation.latitude}, ${gpsLocation.longitude}`,
                    )}
                    {gpsLocation.timestamp ? (
                      <span className="text-gray-400 text-xs ml-1">
                        ({formatRelativeDate(String(gpsLocation.timestamp))})
                      </span>
                    ) : null}
                  </span>
                }
                icon={MapPin}
              />
            )}
          </Section>

          {/* Astro Profile Section */}
          {user.has_astro_profile && (
            <Section title={t.drawer?.astroProfile || "星盘信息"} icon={Star}>
              <Field
                label={t.drawer?.realName || "真实姓名"}
                value={user.real_name}
              />
              <Field label={t.drawer?.gender || "性别"} value={user.gender} />
              <Field
                label={t.drawer?.birthDate || "出生日期"}
                value={user.birth_date}
                icon={Calendar}
              />
              <Field
                label={t.drawer?.birthTime || "出生时间"}
                value={user.birth_time}
                icon={Clock}
              />
              <Field
                label={t.drawer?.birthLocation || "出生地点"}
                value={user.birth_location}
                icon={MapPin}
              />
              {isEditingBirthTz ? (
                <div className="flex items-center gap-2 py-0.5">
                  <span className="text-xs text-gray-500 shrink-0">{t.drawer?.birthTimezone || "出生时区"}</span>
                  <input
                    type="text"
                    value={birthTzInput}
                    onChange={(e) => setBirthTzInput(e.target.value)}
                    placeholder="Asia/Shanghai"
                    className="flex-1 text-sm border border-gray-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Escape") setIsEditingBirthTz(false);
                      if (e.key === "Enter" && birthTzInput.trim() && !isSavingBirthTz) handleSaveBirthTz();
                    }}
                  />
                  <button onClick={handleSaveBirthTz} disabled={isSavingBirthTz || !birthTzInput.trim()} className="text-green-600 hover:text-green-700 disabled:text-gray-300 p-1 shrink-0">
                    <Check className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => setIsEditingBirthTz(false)} className="text-gray-400 hover:text-gray-600 p-1 shrink-0">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between group">
                  <Field label={t.drawer?.birthTimezone || "出生时区"} value={user.birth_timezone} />
                  {user.astro_profile_id && (
                    <button onClick={() => { setBirthTzInput(user.birth_timezone || ""); setIsEditingBirthTz(true); }} className="opacity-0 group-hover:opacity-100 text-blue-400 hover:text-blue-600 p-1 transition-opacity">
                      <Pencil className="w-3 h-3" />
                    </button>
                  )}
                </div>
              )}
              {/* Residence Location - always visible, editable */}
              <div className="pt-2 mt-2 border-t border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Home className="w-3 h-3" />
                    {t.drawer?.residenceInfo || "当前居住地"}
                  </p>
                  {user.astro_profile_id && !isEditingResidence && (
                    <button
                      onClick={() => {
                        setResidenceInput(user.residence_location || "");
                        setIsEditingResidence(true);
                      }}
                      className="text-xs text-blue-500 hover:text-blue-700 flex items-center gap-0.5"
                    >
                      <Pencil className="w-3 h-3" />
                      {t.drawer?.edit || "编辑"}
                    </button>
                  )}
                </div>
                {isEditingResidence ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={residenceInput}
                      onChange={(e) => setResidenceInput(e.target.value)}
                      placeholder={
                        t.drawer?.residencePlaceholder ||
                        "输入居住地，如：上海市浦东新区"
                      }
                      className="flex-1 text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Escape") setIsEditingResidence(false);
                        if (e.key === "Enter" && residenceInput.trim()) {
                          handleSaveResidence();
                        }
                      }}
                    />
                    <button
                      onClick={handleSaveResidence}
                      disabled={isSavingResidence || !residenceInput.trim()}
                      className="text-green-600 hover:text-green-700 disabled:text-gray-300 p-1 shrink-0 w-6 h-6 flex items-center justify-center"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setIsEditingResidence(false)}
                      className="text-gray-400 hover:text-gray-600 p-1 shrink-0 w-6 h-6 flex items-center justify-center"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Field
                      label={t.drawer?.residenceLocation || "居住地点"}
                      value={user.residence_location}
                      icon={MapPin}
                    />
                    {isEditingResidenceTz ? (
                      <div className="flex items-center gap-2 py-0.5">
                        <span className="text-xs text-gray-500 shrink-0">{t.drawer?.residenceTimezone || "居住时区"}</span>
                        <input
                          type="text"
                          value={residenceTzInput}
                          onChange={(e) => setResidenceTzInput(e.target.value)}
                          placeholder="America/Los_Angeles"
                          className="flex-1 text-sm border border-gray-300 rounded px-2 py-0.5 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Escape") setIsEditingResidenceTz(false);
                            if (e.key === "Enter" && residenceTzInput.trim() && !isSavingResidenceTz) handleSaveResidenceTz();
                          }}
                        />
                        <button onClick={handleSaveResidenceTz} disabled={isSavingResidenceTz || !residenceTzInput.trim()} className="text-green-600 hover:text-green-700 disabled:text-gray-300 p-1 shrink-0">
                          <Check className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => setIsEditingResidenceTz(false)} className="text-gray-400 hover:text-gray-600 p-1 shrink-0">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between group">
                        <Field label={t.drawer?.residenceTimezone || "居住时区"} value={user.residence_timezone} />
                        {user.astro_profile_id && (
                          <button onClick={() => { setResidenceTzInput(user.residence_timezone || ""); setIsEditingResidenceTz(true); }} className="opacity-0 group-hover:opacity-100 text-blue-400 hover:text-blue-600 p-1 transition-opacity">
                            <Pencil className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </Section>
          )}
        </div>

        {/* Footer Actions */}
        <div className="p-4 border-t bg-gray-50">
          <div className="flex flex-wrap gap-2">
            {/* Resources Button */}
            {onOpenResources && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onOpenResources(user.user_id, user.display_name)}
              >
                <FolderOpen className="w-4 h-4 mr-1" />
                {t.actions.viewResources}
              </Button>
            )}

            {/* Subscription Button */}
            {onOpenSubscription && (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  onOpenSubscription(user.user_id, user.display_name)
                }
              >
                <CreditCard className="w-4 h-4 mr-1" />
                {t.actions.manageSubscription}
              </Button>
            )}

            {/* System Role Selector */}
            {onUpdateSystemRole && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">
                  <Shield className="w-4 h-4 inline mr-1" />
                  System Role:
                </span>
                <select
                  value={user.system_role || 'end_user'}
                  onChange={(e) => onUpdateSystemRole(user.user_id, e.target.value)}
                  className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="end_user">End User</option>
                  <option value="beta_tester">Beta Tester</option>
                  <option value="developer">Developer</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            )}

            {/* Legacy Beta Toggle (kept for quick access) */}
            {onToggleBeta && (
              <Button
                variant={user.is_beta_tester ? "outline" : "ghost"}
                size="sm"
                onClick={() => onToggleBeta(user.user_id, !user.is_beta_tester)}
                className={
                  user.is_beta_tester ? "border-purple-300 text-purple-700" : ""
                }
              >
                {user.is_beta_tester ? (
                  <>
                    <UserX className="w-4 h-4 mr-1" />
                    {t.actions.removeBeta}
                  </>
                ) : (
                  <>
                    <UserCheck className="w-4 h-4 mr-1" />
                    {t.actions.makeBeta}
                  </>
                )}
              </Button>
            )}

            {/* Legacy Admin Toggle (kept for quick access) */}
            {onToggleAdmin && (
              <Button
                variant={user.is_admin ? "outline" : "ghost"}
                size="sm"
                onClick={() => onToggleAdmin(user.user_id, !user.is_admin)}
                className={user.is_admin ? "border-red-300 text-red-700" : ""}
              >
                {user.is_admin ? (
                  <>
                    <ShieldOff className="w-4 h-4 mr-1" />
                    {t.actions.revokeAdmin}
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4 mr-1" />
                    {t.actions.makeAdmin}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default UserDetailDrawer;
